import { sendGAEvent } from '@next/third-parties/google';
import { omit } from 'lodash';

export enum GA_EVENT {
	FORM_SEARCH_SUBMIT = 'search_submission',
	SEARCH_FIELD_CLICK = 'location_click',
	LOCATION_ITEM_SELECT = 'location_item_select',
	DATE_CLICK = 'date_click',
	FROM_DATE = 'from_date',
	TO_DATE = 'to_date',
	GUESTS_CLICK = 'guest_click',
	GUESTS_SELECT = 'guests_select',
	SEARCH_WITHOUT_LOCATION = 'search_without_location',
	SIGN_UP_DEALS = 'sign_up_deals',
	VERTICAL_CLICK = 'vertical_click',
	PRICE_ALERT_SELECT = 'price_alert_select',
	// Flight specific events from TPD-539
	FROM_CLICK = 'from_click',
	FROM_DATA_CLICK = 'from_data_click',
	TO_CLICK = 'to_click',
	TO_DATA_CLICK = 'to_data_click',
	ONE_WAY_CLICK = 'one_way_click',
	ROUND_TRIP_CLICK = 'round_trip_click',
	TRAVELERS_CLICK = 'travelers_click',
	TRAVELERS_SELECT = 'travelers_select',
	CABIN_CLASS_CLICK = 'cabin_class_click',
	CABIN_CLASS_SELECT = 'cabin_class_select',
	ADD_HOTEL_CLICK = 'add_hotel_click',
	SIGN_UP_ENABLE = 'sign_up_enable',
	// Car specific events from TPD-540
	DIFFERENT_DROPOFF = 'different_dropoff',
	UNCHECK_DIFFERENT_DROPOFF = 'uncheck_different_dropoff',
	DROPOFF_LOCATION_CLICK = 'dropoff_location_click',
	DROPOFF_LOCATION_DATA_CLICK = 'dropoff_location_data_click',
	PICKUP_DATE = 'pickup_date',
	DROPOFF_DATE = 'dropoff_date',
	PICKUP_TIME = 'pickup_time',
	DROPOFF_TIME = 'dropoff_time',
	LOCATION_DATA_CLICK = 'location_data_click',
	PAGE_VIEW = 'page_view',
}

export enum FB_EVENT {
	SEARCH = 'Search',
	LEAD = 'Lead',
}

export const getVerticalPrefix = (vertical: string) => {
	switch (vertical) {
		case 'hotels':
			return 'hotel_';
		case 'cars':
			return 'car_';
		case 'flights':
			return 'flight_';
		case 'cruises':
			return 'cruise_';
		default:
			return '';
	}
};

export const emitGAEvent = (eventName: string, vertical: string, data?: any) => {
	const prefix = getVerticalPrefix(vertical);
	const fullEventName = `${prefix}${eventName}`;

	if (typeof window !== 'undefined') {
		sendGAEvent('event', fullEventName, { ...data });
	}
};

export const emitPageViewGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.PAGE_VIEW, vertical);
};

export const emitLocationItemSelectGAEvent = (vertical: string, item: any) => {
	emitGAEvent(
		GA_EVENT.LOCATION_ITEM_SELECT,
		vertical,
		omit(item, ['image', 'id', 'documentId'])
	);
};

export const emitSearchFieldClickGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.SEARCH_FIELD_CLICK, vertical);
};

export const emitDateClickGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.DATE_CLICK, vertical);
};

export const emitFromDateGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.FROM_DATE, vertical);
};

export const emitToDateGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.TO_DATE, vertical);
};

export const emitGuestsClickGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.GUESTS_CLICK, vertical);
};

export const emitGuestsSelectGAEvent = (vertical: string, data: { rooms: number; adults: number; children: number; total: number }) => {
	emitGAEvent(GA_EVENT.GUESTS_SELECT, vertical, data);
};

export const emitSearchWithoutLocationGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.SEARCH_WITHOUT_LOCATION, vertical);
};

export const emitSignUpDealsGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.SIGN_UP_DEALS, vertical);
};

export const emitVerticalClickGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.VERTICAL_CLICK, vertical);
};

export const emitPriceAlertSelectGAEvent = (vertical: string) => {
	emitGAEvent(GA_EVENT.PRICE_ALERT_SELECT, vertical);
};

export const emitFromClickGAEvent = () => {
	emitGAEvent(GA_EVENT.FROM_CLICK, 'flights');
};

export const emitFromDataClickGAEvent = (data: any) => {
	emitGAEvent(GA_EVENT.FROM_DATA_CLICK, 'flights', data);
};

export const emitToClickGAEvent = () => {
	emitGAEvent(GA_EVENT.TO_CLICK, 'flights');
};

export const emitToDataClickGAEvent = (data: any) => {
	emitGAEvent(GA_EVENT.TO_DATA_CLICK, 'flights', data);
};

export const emitOneWayClickGAEvent = () => {
	emitGAEvent(GA_EVENT.ONE_WAY_CLICK, 'flights');
};

export const emitRoundTripClickGAEvent = () => {
	emitGAEvent(GA_EVENT.ROUND_TRIP_CLICK, 'flights');
};

export const emitTravelersClickGAEvent = () => {
	emitGAEvent(GA_EVENT.TRAVELERS_CLICK, 'flights');
};

export const emitTravelersSelectGAEvent = (data: any) => {
	emitGAEvent(GA_EVENT.TRAVELERS_SELECT, 'flights', data);
};

export const emitCabinClassClickGAEvent = () => {
	emitGAEvent(GA_EVENT.CABIN_CLASS_CLICK, 'flights');
};

export const emitCabinClassSelectGAEvent = (data: any) => {
	emitGAEvent(GA_EVENT.CABIN_CLASS_SELECT, 'flights', data);
};

export const emitAddHotelClickGAEvent = () => {
	emitGAEvent(GA_EVENT.ADD_HOTEL_CLICK, 'flights');
};

export const emitSignUpEnableGAEvent = () => {
	emitGAEvent(GA_EVENT.SIGN_UP_ENABLE, 'flights');
};

export const emitCarFromClickGAEvent = () => {
	emitGAEvent(GA_EVENT.FROM_CLICK, 'cars');
};

export const emitCarFromDataClickGAEvent = (data: any) => {
	emitGAEvent(GA_EVENT.LOCATION_DATA_CLICK, 'cars', data);
};

export const emitCarToClickGAEvent = () => {
	emitGAEvent(GA_EVENT.TO_CLICK, 'cars');
};

export const emitCarToDataClickGAEvent = (data: any) => {
	emitGAEvent(GA_EVENT.TO_DATA_CLICK, 'cars', data);
};

export const emitCarDifferentDropoffGAEvent = () => {
	emitGAEvent(GA_EVENT.DIFFERENT_DROPOFF, 'cars');
};

export const emitCarUncheckDifferentDropoffGAEvent = () => {
	emitGAEvent(GA_EVENT.UNCHECK_DIFFERENT_DROPOFF, 'cars');
};

export const emitCarDropoffLocationClickGAEvent = () => {
	emitGAEvent(GA_EVENT.DROPOFF_LOCATION_CLICK, 'cars');
};

export const emitCarDropoffLocationDataClickGAEvent = (data: any) => {
	emitGAEvent(GA_EVENT.DROPOFF_LOCATION_DATA_CLICK, 'cars', data);
};

export const emitCarDateClickGAEvent = () => {
	emitGAEvent(GA_EVENT.DATE_CLICK, 'cars');
};

export const emitCarFromDateGAEvent = () => {
	emitGAEvent(GA_EVENT.PICKUP_DATE, 'cars');
};

export const emitCarToDateGAEvent = () => {
	emitGAEvent(GA_EVENT.DROPOFF_DATE, 'cars');
};

export const emitCarFromTimeGAEvent = (time: string) => {
	emitGAEvent(GA_EVENT.PICKUP_TIME, 'cars', { time });
};

export const emitCarToTimeGAEvent = (time: string) => {
	emitGAEvent(GA_EVENT.DROPOFF_TIME, 'cars', { time });
};

export const emitGoogleSignInGAEvent = (userData?: any) => {
	if (typeof window !== 'undefined') {
		sendGAEvent('event', 'google_sign_in', {
			user_id: userData?.sub || null,
			user_email: userData?.email || null,
			user_name: userData?.name || null,
			email_verified: userData?.email_verified || false,
			sign_in_method: 'Google One Tap',
			timestamp: new Date().toISOString(),
		});
	}
};

export const emitLoginGAEvent = (method: string, userId?: string) => {
	if (typeof window !== 'undefined') {
		sendGAEvent('event', 'login', {
			method,
			user_id: userId || null,
		});
	}
};

export const emitLogoutGAEvent = (method: string) => {
	if (typeof window !== 'undefined') {
		sendGAEvent('event', 'logout', {
			method,
		});
	}
};

export const emitGAEventSetUserId = (id: string) => {
	if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
		window.gtag('set', {
			user_id: id,
		});
	}
};

// Pixel id shared with <FBPixelScript> so late advanced-matching updates target
// the same pixel. Override via NEXT_PUBLIC_FB_PIXEL_ID.
export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID || '2354192498230538';

export const emitFBEventSetUserId = (id: string) => {
	if (typeof window !== 'undefined' && (window as any).fbq) {
		// Attach advanced-matching external_id by re-calling init with the updated
		// params (Meta's documented manual AM pattern; init never re-fires PageView).
		// The previous `fbq('set','userData',{external_id})` form omits the pixel id,
		// so Meta dropped it with an "invalid pixel_id" console warning and the
		// external_id was never actually set (TPD-573 re-audit).
		(window as any).fbq('init', FB_PIXEL_ID, { external_id: id });
	}
};

// Per-event id so Facebook can dedupe the browser pixel hit against the matching
// server CAPI event (the server MUST echo the same id) and against accidental
// browser double-fires. TPD-573 Phase 7 / migration inventory: the ported FB
// events previously sent no eventID, breaking CAPI/browser dedup.
const generateFBEventId = (): string =>
	typeof window !== 'undefined' && (window as any).crypto?.randomUUID
		? (window as any).crypto.randomUUID()
		: `fb-${Date.now()}-${Math.round(Math.random() * 1e9)}`;

export const emitFBEvent = (eventName: string, data?: any, eventID?: string) => {
	if (typeof window !== 'undefined' && (window as any).fbq) {
		(window as any).fbq('track', eventName, data, { eventID: eventID || generateFBEventId() });
	}
};

export const emitPageViewFBEvent = () => {
	emitFBEvent('PageView');
};

export const emitSearchEventFBEvent = (data: any, eventID?: string) => {
	emitFBEvent(FB_EVENT.SEARCH, data, eventID);
};

// FB Lead on flight search — parity with the removed GTM tag 14 ("FB Lead - Search Flights").
export const emitLeadFBEvent = (data: any, eventID?: string) => {
	emitFBEvent(FB_EVENT.LEAD, data, eventID);
};

// --- Twitter / X pixel (oh4k9) — parity with the removed GTM tags 212/213/214 ---
// Per-vertical search conversion event ids (from GTM-PSL3ZCK_v109).
export const TWITTER_EVENT = {
	HOTEL_SEARCH: 'tw-oh4k9-oh4kl',
	FLIGHT_SEARCH: 'tw-oh4k9-oh4kw',
	CAR_SEARCH: 'tw-oh4k9-oh4l1',
};

export const emitTwitterEvent = (eventId: string, params?: any) => {
	if (typeof window !== 'undefined' && (window as any).twq) {
		(window as any).twq('event', eventId, params || {});
	}
};

// --- GTM-ported raw GA4 events (TPD-573 Phase 7 / TPD-560) ---
// These were minted only by GTM gaawe tags; ported here so they survive GTM
// removal. Raw names (no vertical prefix) via emitGAEvent(name, '') and no custom
// params. See docs/gtm-ga4-event-reconciliation.md.

// Phone-number clicks (parity with GTM tags 162/115/158/160).
export const emitAnyClickPhoneGAEvent = () => emitGAEvent('any_click_phone', '');
export const emitClickPhoneTopMobileGAEvent = () => emitGAEvent('click_phone_top_mobile', '');
export const emitClickPhoneTopDesktopGAEvent = () => emitGAEvent('click_phone_top_desktop', '');
export const emitClickPhoneBottomMobileGAEvent = () => emitGAEvent('click_phone_bottom_mobile', '');

// Newsletter + email-filled (parity with GTM tags 150/145/144).
export const emitNewsletterSubmissionGAEvent = () => emitGAEvent('ga4_form_newsletter_submission', '');
export const emitSearchEmailFilledGAEvent = () => emitGAEvent('ga4_search_email_filled', '');
export const emitNotifyEmailFilledGAEvent = () => emitGAEvent('ga4_notify_email_filled', '');

// Passthru no-results timers (parity with GTM tags 140/154). The '=2' is part of
// the literal GA4 event name, not a param.
export const emitFlightNoResultGAEvent = () => emitGAEvent('ga4_flight_no_result', '');
export const emitNoResultPassthru2GAEvent = () => emitGAEvent('ga4_no_result_passthru=2', '');

// Cruises form interactions (parity with GTM tags 174/186/180/178/201/196/192/188/203/208/206/184/204).
// --- TPD Cruises events (TPD-542) — clean cruise_* schema, one clear event per action ---
// Reused shared helpers (already produce the right cruise_* name): location field click =>
// emitSearchFieldClickGAEvent('cruises') (cruise_location_click); month field click =>
// emitDateClickGAEvent('cruises') (cruise_date_click); no-location => emitSearchWithoutLocationGAEvent('cruises');
// vertical click => emitVerticalClickGAEvent('cruises'); search submit => emitGAEvent(FORM_SEARCH_SUBMIT,'cruises').
// Cruise-only events below:
export const emitCruiseLocationDataClickGAEvent = (data: { country?: string; city?: string; name?: string }) =>
	emitGAEvent('location_data_click', 'cruises', data);
export const emitCruiseDepartureMonthsClickGAEvent = () => emitGAEvent('departure_months_click', 'cruises');
export const emitCruiseDepartureMonthsDataClickGAEvent = (data: { selected_months: string }) =>
	emitGAEvent('departure_months_data_click', 'cruises', data);
export const emitCruiseDepartureMonthsDoneGAEvent = () => emitGAEvent('departure_months_done', 'cruises');
export const emitCruiseDurationClickGAEvent = () => emitGAEvent('duration', 'cruises');
export const emitCruiseDurationDataGAEvent = (data: { selected_length?: string }) =>
	emitGAEvent('duration_data', 'cruises', data);
export const emitCruiseTravelersClickGAEvent = () => emitGAEvent('travelers_click', 'cruises');
export const emitCruiseTravelersDataClickGAEvent = (data: { adults: number; children: number }) =>
	emitGAEvent('travelers_data_click', 'cruises', data);
export const emitCruiseTravelersDoneGAEvent = () => emitGAEvent('travelers_done', 'cruises');
export const emitCruiseNewsletterSubscribeGAEvent = () => emitGAEvent('newsletter_subscribe', 'cruises');
// Out of TPD-542 form-event scope (content carousel) — kept as-is, flagged for product review.
export const emitCruisesCarouselClickGAEvent = () => emitGAEvent('ga4_cruises_carousel_click', '');
